import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchAISettings, updateAISettings } from '../api';
import type { AISettings } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PROVIDERS = [
  { id: 'ollama', label: 'Ollama (Local)', description: 'Local model via Ollama native API' },
  { id: 'openai', label: 'OpenAI-Compatible', description: 'LM Studio / Ollama OpenAI mode / vLLM / OpenAI' },
  { id: 'groq', label: 'Groq', description: 'Fast inference API' },
  { id: 'gemini', label: 'Gemini', description: 'Google AI' },
  { id: 'openrouter', label: 'OpenRouter', description: 'Multi-model gateway' },
  { id: 'cloudflareai', label: 'Cloudflare AI', description: 'Workers AI binding' },
];

export function AISettingsModal({ open, onClose }: Props) {
  const [settings, setSettings] = useState<AISettings>({
    provider: 'ollama',
    baseUrl: '',
    model: '',
    apiKey: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAISettings();
      if (res.ok) setSettings(res.settings);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
      setSuccess(false);
    }
  }, [open, load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await updateAISettings(settings);
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const needsApiKey = ['groq', 'gemini', 'openrouter'].includes(settings.provider);
  const needsBaseUrl = settings.provider === 'ollama' || settings.provider === 'openai';
  const openaiOptionalKey = settings.provider === 'openai';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-100">
            🤖 AI Provider Settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-300"
          >
            Close
          </button>
        </div>

        {error && (
          <p className="mb-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
            {error}
          </p>
        )}

        {success && (
          <p className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
            Settings saved successfully!
          </p>
        )}

        {loading ? (
          <div className="flex h-32 items-center justify-center text-sm text-zinc-500">
            Loading...
          </div>
        ) : (
          <div className="space-y-4">
            {/* Provider selector */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-400">
                AI Provider
              </label>
              <div className="grid grid-cols-1 gap-1.5">
                {PROVIDERS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, provider: p.id }))}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left transition ${
                      settings.provider === p.id
                        ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
                        : 'border-zinc-800 bg-zinc-800/40 text-zinc-400 hover:border-zinc-700'
                    }`}
                  >
                    <span className="text-xs font-medium">{p.label}</span>
                    <span className="text-[10px] text-zinc-600">{p.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Base URL (Ollama / OpenAI-compatible) */}
            {needsBaseUrl && (
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-400">
                  Base URL
                </label>
                <input
                  type="text"
                  value={settings.baseUrl ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, baseUrl: e.target.value }))}
                  placeholder={settings.provider === 'openai' ? 'http://localhost:1234/v1' : 'http://localhost:11434'}
                  className="w-full rounded-lg border border-slate-700 bg-zinc-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
                />
                <p className="mt-1 text-[10px] text-zinc-600">
                  {settings.provider === 'openai'
                    ? 'OpenAI-compatible endpoint root, e.g. http://localhost:1234/v1 (LM Studio) or https://api.openai.com/v1'
                    : 'Ollama server URL, e.g. http://localhost:11434'}
                </p>
              </div>
            )}

            {/* Model */}
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">
                Model {settings.provider === 'ollama' ? '(optional)' : ''}
              </label>
              <input
                type="text"
                value={settings.model ?? ''}
                onChange={(e) => setSettings((s) => ({ ...s, model: e.target.value }))}
                placeholder={
                  settings.provider === 'ollama' ? 'qwen2.5:7b-instruct-q4_K_M' :
                  settings.provider === 'groq' ? 'llama-3.3-70b-versatile' :
                  settings.provider === 'gemini' ? 'gemini-2.0-flash' :
                  settings.provider === 'openrouter' ? 'meta-llama/llama-3.3-70b-instruct' :
                  '@cf/meta/llama-3.1-8b-instruct'
                }
                className="w-full rounded-lg border border-slate-700 bg-zinc-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
              />
            </div>

            {/* API Key */}
            {(needsApiKey || openaiOptionalKey) && (
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-400">
                  API Key {openaiOptionalKey ? '(optional — skip for local LM Studio/Ollama)' : ''}
                </label>
                <input
                  type="password"
                  value={settings.apiKey ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, apiKey: e.target.value }))}
                  placeholder={openaiOptionalKey ? 'sk-... (leave blank for local)' : 'sk-...'}
                  className="w-full rounded-lg border border-slate-700 bg-zinc-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
                />
              </div>
            )}

            {/* Save button */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
